import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { wikiApi, chatApi } from "../api/client";
import type { WikiPage, WikiIssue, Chat } from "../types";
import Markdown from "../components/Markdown";
import "./WikiView.css";
import { useToast } from "../lib/toast";

export default function WikiView() {
  const toast = useToast();
  const { projectId, pageId } = useParams();
  const navigate = useNavigate();
  const [pages, setPages] = useState<WikiPage[]>([]);
  const [current, setCurrent] = useState<WikiPage | null>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [creating, setCreating] = useState(false);
  const [linting, setLinting] = useState(false);
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState("");
  const [generatingProject, setGeneratingProject] = useState(false);
  const [generatingChat, setGeneratingChat] = useState(false);

  const loadList = async () => {
    if (!projectId) return;
    try {
      setPages(await wikiApi.list(projectId));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const loadChats = async () => {
    if (!projectId) return;
    try {
      setChats(await chatApi.list(projectId));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const loadPage = async () => {
    if (!projectId || !pageId) {
      setCurrent(null);
      return;
    }
    try {
      const p = await wikiApi.get(projectId, pageId);
      setCurrent(p);
      setTitle(p.title);
      setContent(p.content);
      setEditing(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    loadList();
    loadChats();
  }, [projectId]);

  useEffect(() => {
    loadPage();
  }, [projectId, pageId]);

  const createPage = async () => {
    if (!projectId || !title.trim()) return;
    try {
      const p = await wikiApi.create(projectId, { title, content });
      setCreating(false);
      setContent("");
      setTitle("");
      await loadList();
      navigate(`/projects/${projectId}/wiki/${p.id}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const save = async () => {
    if (!projectId || !pageId) return;
    try {
      await wikiApi.update(projectId, pageId, { title, content });
      await loadPage();
      loadList();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const lint = async () => {
    if (!projectId || !pageId) return;
    setLinting(true);
    try {
      await wikiApi.lint(projectId, pageId);
      await loadPage();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLinting(false);
    }
  };

  const remove = async () => {
    if (!projectId || !pageId || !confirm("Delete this page?")) return;
    await wikiApi.remove(projectId, pageId);
    navigate(`/projects/${projectId}/wiki`);
    loadList();
  };

  /** targetPageId regenerates that existing page in place; omitted creates
   * a new page and navigates to it. */
  const generateFromProject = async (targetPageId?: string) => {
    if (!projectId) return;
    setGeneratingProject(true);
    try {
      const p = await wikiApi.generate(projectId, { mode: "project", page_id: targetPageId });
      await loadList();
      if (targetPageId) await loadPage();
      else navigate(`/projects/${projectId}/wiki/${p.id}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setGeneratingProject(false);
    }
  };

  const generateFromChat = async (targetPageId?: string) => {
    if (!projectId || !selectedChatId) return;
    setGeneratingChat(true);
    try {
      const p = await wikiApi.generate(projectId, { mode: "chat", chat_id: selectedChatId, page_id: targetPageId });
      await loadList();
      if (targetPageId) await loadPage();
      else navigate(`/projects/${projectId}/wiki/${p.id}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setGeneratingChat(false);
    }
  };

  const issues: WikiIssue[] = (current?.lint_results as { issues?: WikiIssue[] } | null)?.issues || [];

  return (
    <div className="view wiki-view">
      <div className="view-header">
        <div>
          <h2 className="view-title">📖 Wiki</h2>
          <p className="view-subtitle">Karpathy-style project wiki</p>
        </div>
        <div className="row wiki-header-actions">
          <button className="btn btn-sm" onClick={() => generateFromProject()} disabled={generatingProject}>
            {generatingProject ? <span className="spinner" /> : "✨ Generate from Project"}
          </button>
          {projectId && (
            <a className="btn btn-sm" href={wikiApi.exportUrl(projectId)}>
              ⬇ Export Wiki
            </a>
          )}
          <button className="btn btn-primary" onClick={() => { setCreating((c) => !c); setTitle(""); setContent(""); }}>
            {creating ? "Cancel" : "+ New Page"}
          </button>
        </div>
      </div>

      <div className="wiki-layout">
        <aside className="wiki-sidebar card">
          <div className="wiki-generate-chat">
            <select className="input" value={selectedChatId} onChange={(e) => setSelectedChatId(e.target.value)}>
              <option value="">Summarize a chat…</option>
              {chats.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
            <button
              className="btn btn-sm"
              disabled={!selectedChatId || generatingChat}
              onClick={() => generateFromChat()}
            >
              {generatingChat ? <span className="spinner" /> : "Summarize into Wiki"}
            </button>
          </div>
          {pages.length === 0 && <div className="dim" style={{ padding: 12 }}>No pages</div>}
          {pages.map((p) => (
            <div
              key={p.id}
              className={`wiki-page-item ${pageId === p.id ? "active" : ""}`}
              onClick={() => navigate(`/projects/${projectId}/wiki/${p.id}`)}
            >
              <span className="wiki-page-title">{p.title}</span>
              {typeof p.lint_score === "number" && (
                <span className={`tag ${lintTagClass(p.lint_score)}`}>
                  {Math.round(p.lint_score)}
                </span>
              )}
            </div>
          ))}
        </aside>

        <div className="wiki-main">
          {creating && (
            <div className="card fade-in" style={{ marginBottom: "var(--gap-md)" }}>
              <div className="col">
                <input className="input" placeholder="Page title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <textarea className="textarea" style={{ minHeight: 200 }} placeholder="Markdown content…" value={content} onChange={(e) => setContent(e.target.value)} />
                <button className="btn btn-primary" onClick={createPage}>Create Page</button>
              </div>
            </div>
          )}

          {!pageId && !creating && (
            <div className="empty-state">
              <h3>Select a page</h3>
              <p>Choose a wiki page from the list, or create a new one.</p>
            </div>
          )}

          {current && (
            <>
              <div className="card wiki-content-card">
                <div className="row-between" style={{ marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>{editing ? "Editing" : current.title}</h3>
                  <div className="row">
                    <button
                      className="btn btn-sm"
                      onClick={() => generateFromProject(current.id)}
                      disabled={generatingProject}
                      title="Regenerate this page from the project's instructions and files"
                    >
                      {generatingProject ? <span className="spinner" /> : "🔄 Refresh from Project"}
                    </button>
                    {selectedChatId && (
                      <button
                        className="btn btn-sm"
                        onClick={() => generateFromChat(current.id)}
                        disabled={generatingChat}
                        title="Regenerate this page from the selected chat"
                      >
                        {generatingChat ? <span className="spinner" /> : "🔄 Refresh from Chat"}
                      </button>
                    )}
                    {projectId && (
                      <a className="btn btn-sm" href={wikiApi.exportPageUrl(projectId, current.id)}>⬇ Export</a>
                    )}
                    <button className="btn btn-sm" onClick={lint} disabled={linting}>
                      {linting ? <span className="spinner" /> : "Lint"}
                    </button>
                    <button className="btn btn-sm" onClick={() => setEditing((e) => !e)}>
                      {editing ? "Cancel" : "Edit"}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={remove}>Delete</button>
                  </div>
                </div>

                {issues.length > 0 && (
                  <div className="wiki-lint">
                    <div className="wiki-lint-title">Lint issues ({issues.length})</div>
                    {issues.map((iss, i) => (
                      <div key={i} className={`wiki-lint-item ${iss.severity || "warning"}`}>
                        <span className="wiki-lint-sev">{iss.severity || "warning"}</span>
                        <span>{iss.message}</span>
                        {iss.line && <span className="dim"> (line {iss.line})</span>}
                      </div>
                    ))}
                  </div>
                )}

                {editing ? (
                  <div className="col">
                    <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <textarea
                      className="textarea"
                      style={{ minHeight: 360 }}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                    />
                    <button className="btn btn-primary" onClick={save}>Save</button>
                  </div>
                ) : (
                  <Markdown content={current.content} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function lintTagClass(score: number): string {
  // Backend sends 0-100 (project_service.lint_wiki_page's `score`), not
  // 0-1 — these thresholds used to always be true for any score >= 0.8,
  // so the badge rendered green regardless of the real score.
  if (score >= 80) return "tag-success";
  if (score >= 50) return "tag-warning";
  return "tag-danger";
}